import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap rounded-lg text-xs sm:text-sm font-semibold ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 sm:[&_svg]:size-4 [&_svg]:shrink-0 transition-all duration-300 ease-out touch-manipulation",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-primary-foreground shadow-md hover:shadow-lg active:scale-[0.98] sm:hover:scale-[1.02] sm:hover:brightness-110",
        destructive: "bg-gradient-to-r from-red-600 to-red-500 text-destructive-foreground shadow-md hover:shadow-lg hover:from-red-500 hover:to-red-400 active:scale-[0.98] sm:hover:scale-[1.02]",
        outline: "border-2 border-primary/30 bg-background/50 backdrop-blur-sm hover:bg-primary/10 hover:border-primary hover:text-primary hover:shadow-md active:scale-[0.98]",
        secondary: "bg-gradient-secondary text-secondary-foreground shadow-md hover:shadow-lg active:scale-[0.98] sm:hover:scale-[1.02] sm:hover:brightness-110",
        ghost: "hover:bg-accent/20 hover:text-accent-foreground hover:shadow-sm active:scale-[0.98]",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary/80",
        gradient: "bg-gradient-primary text-primary-foreground shadow-md hover:shadow-lg active:scale-95 sm:hover:scale-105 sm:hover:brightness-110",
        hero: "bg-gradient-hero text-primary-foreground shadow-lg hover:shadow-xl active:scale-95 sm:hover:scale-105 sm:hover:brightness-110",
        sunset: "bg-gradient-sunset text-primary-foreground shadow-md hover:shadow-lg active:scale-[0.98] sm:hover:scale-[1.02] sm:hover:brightness-110",
      },
      size: {
        default: "h-10 sm:h-11 px-4 sm:px-5 py-2 sm:py-2.5",
        sm: "h-8 sm:h-9 rounded-md px-3 sm:px-4",
        lg: "h-11 sm:h-12 rounded-lg px-6 sm:px-8",
        xl: "h-12 sm:h-14 rounded-xl px-6 sm:px-10 text-sm sm:text-base",
        icon: "h-9 w-9 sm:h-10 sm:w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
